import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Loader2, Layers } from "lucide-react";
import {
  useEpics,
  useCreateEpic,
  useUpdateEpic,
  useDeleteEpic,
  type ProjectBacklogItem,
} from "@/hooks/useProjectPlanning";
import { EPIC_STATUS_META, nextEpicStatus } from "@/lib/statusTone";

export function EpicsPanel({
  clientProjectId,
  items,
}: {
  clientProjectId: string;
  items: ProjectBacklogItem[];
}) {
  const { data: epics = [], isLoading } = useEpics(clientProjectId);
  const createEpic = useCreateEpic(clientProjectId);
  const updateEpic = useUpdateEpic(clientProjectId);
  const deleteEpic = useDeleteEpic(clientProjectId);
  const [newTitle, setNewTitle] = useState("");

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    createEpic.mutate({ title: newTitle.trim() }, { onSuccess: () => setNewTitle("") });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCreate();
            }
          }}
          placeholder="Novo épico..."
        />
        <Button onClick={handleCreate} disabled={createEpic.isPending || !newTitle.trim()}>
          <Plus className="mr-1.5 h-4 w-4" /> Criar
        </Button>
      </div>

      {epics.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          <Layers className="mx-auto mb-2 h-8 w-8 opacity-40" />
          Nenhum épico criado ainda.
        </div>
      ) : (
        <div className="space-y-2">
          {epics.map((epic) => {
            const epicItems = items.filter((i) => i.epic_id === epic.id);
            const done = epicItems.filter((i) => i.status === "done").length;
            const total = epicItems.length;
            const progress = total > 0 ? Math.round((done / total) * 100) : 0;
            return (
              <Card key={epic.id}>
                <CardContent className="space-y-2 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{epic.title}</p>
                      {epic.description && (
                        <p className="line-clamp-1 text-xs text-muted-foreground">
                          {epic.description}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        title={`Mudar para ${EPIC_STATUS_META[nextEpicStatus(epic.status)].label.toLowerCase()}`}
                        onClick={() =>
                          updateEpic.mutate({
                            id: epic.id,
                            data: { status: nextEpicStatus(epic.status) },
                          })
                        }
                      >
                        <StatusBadge tone={EPIC_STATUS_META[epic.status].tone}>
                          {EPIC_STATUS_META[epic.status].label}
                        </StatusBadge>
                      </button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive"
                        onClick={() => deleteEpic.mutate(epic.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={progress} className="h-1.5 flex-1" />
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {done}/{total}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
